import type { FC, MouseEvent } from 'react';
import { memo, useCallback, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import { Button, Divider, Menu, MenuItem, Typography } from '@mui/material';

interface SavedFilterDropdownProps {
  label: string;
  isDirty: boolean;
  disabled?: boolean;
  onUpdate: () => void;
  onDelete: () => void;
}

const SavedFilterDropdown: FC<SavedFilterDropdownProps> = ({
  label,
  isDirty,
  disabled = false,
  onUpdate,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (disabled) return;
      setAnchorEl(event.currentTarget);
    },
    [disabled],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleUpdate = useCallback(() => {
    onUpdate();
    handleClose();
  }, [handleClose, onUpdate]);

  const handleDelete = useCallback(() => {
    handleClose();
    onDelete();
  }, [handleClose, onDelete]);

  return (
    <>
      <Button
        size="small"
        variant="text"
        color={isDirty && !disabled ? 'primary' : 'inherit'}
        disabled={disabled}
        endIcon={<KeyboardArrowDownIcon fontSize="small" />}
        onClick={handleOpen}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        aria-label={isDirty && !disabled ? `${label} *` : label}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          px: 1,
        }}
      >
        {label}
        {isDirty && !disabled ? ' *' : ''}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        elevation={8}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { minWidth: 200 },
          },
        }}
      >
        {isDirty && (
          <MenuItem
            onClick={handleUpdate}
            disableTouchRipple
            disableRipple
            sx={(t) => ({ color: t.palette.action.active, py: 1.25, gap: 1.25 })}
          >
            <SyncOutlinedIcon sx={{ color: 'action.active' }} fontSize="medium" />
            <Typography
              sx={(t) => ({
                fontSize: t.typography.body2.fontSize,
                color: t.palette.text.primary,
                fontWeight: 400,
              })}
            >
              Update
            </Typography>
          </MenuItem>
        )}
        {isDirty && (
          <Divider sx={{ '&.MuiDivider-root.MuiDivider-fullWidth': { mt: 0, mb: 0 } }} />
        )}
        <MenuItem
          onClick={handleDelete}
          disableTouchRipple
          disableRipple
          sx={(t) => ({ color: t.palette.action.active, py: 1.25, gap: 1.25 })}
        >
          <DeleteOutlineIcon sx={{ color: 'action.active' }} fontSize="medium" />
          <Typography
            sx={(t) => ({
              fontSize: t.typography.body2.fontSize,
              color: t.palette.text.primary,
              fontWeight: 400,
            })}
          >
            Delete
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default memo(SavedFilterDropdown);
